/**
 * Reclaim · Public Share — Morning Activation Express
 *
 * UNAUTHENTICATED landing page. A member shares this URL with a friend
 * (text / WhatsApp / DM) and the friend can listen to the same 3-min
 * Morning Activation Express that opens the member's day. Brand-safe,
 * minimal, with a soft "Try Reclaim" CTA at the bottom.
 *
 * Route: /share/morning-activation
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sun, Play, Pause, Loader2, Sparkles } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function MorningActivationShare() {
  const navigate = useNavigate();
  const audioRef = useRef(null);
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    fetch(`${API}/api/public/daily-essentials/morning-activation/info`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setInfo)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setProgress(a.duration ? a.currentTime / a.duration : 0);
    const onEnd = () => { setPlaying(false); setProgress(1); };
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("ended", onEnd);
    };
  }, [info]);

  const toggle = () => {
    if (!audioRef.current) return;
    if (playing) { audioRef.current.pause(); setPlaying(false); }
    else { audioRef.current.play().then(() => setPlaying(true)).catch(() => {}); }
  };

  const totalSec = info?.duration_seconds || 180;
  const elapsed = Math.floor(progress * totalSec);
  const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0B0805] via-[#100B07] to-[#080604] text-white flex flex-col">
      <header className="px-5 pt-6 pb-4 flex items-center justify-center">
        <div className="flex items-center gap-2 text-amber-200/85">
          <Sun size={18} />
          <span className="text-[11px] uppercase tracking-[0.3em]">Reclaim</span>
        </div>
      </header>

      <main className="flex-1 px-5 py-8 max-w-md w-full mx-auto flex flex-col">
        {/* Hero */}
        <div className="text-center mb-8 mt-4">
          <p className="text-amber-200/70 text-[10px] uppercase tracking-[0.32em] mb-3">A gift from a friend</p>
          <h1 className="text-4xl sm:text-5xl text-amber-50/95 font-light leading-tight" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}>
            Morning Activation
          </h1>
          <p className="text-white/55 text-sm mt-3 italic max-w-xs mx-auto leading-relaxed" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}>
            Three minutes to begin the day consciously. Walk outside. Receive the light. Breathe.
          </p>
        </div>

        {/* Player card */}
        <div className="rounded-3xl bg-gradient-to-br from-amber-500/15 via-yellow-500/8 to-transparent ring-1 ring-amber-400/25 p-6">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-white/45">
              <Loader2 size={20} className="animate-spin" />
            </div>
          ) : !info?.ready ? (
            <p className="text-center text-white/55 text-sm py-8">Audio is being prepared. Check back shortly.</p>
          ) : (
            <>
              <div className="flex items-center gap-4">
                <button
                  onClick={toggle}
                  data-testid="share-play"
                  className={`w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0 shadow-lg shadow-amber-500/15 transition ${
                    playing ? "bg-amber-300" : "bg-amber-400/95 hover:bg-amber-300"
                  } text-[#0B0805]`}
                >
                  {playing ? <Pause size={22} /> : <Play size={22} className="translate-x-[2px]" />}
                </button>
                <div className="flex-1">
                  <p className="text-amber-100 text-sm tracking-wide font-medium">
                    {info.title || "Morning Activation Express"}
                  </p>
                  <p className="text-white/45 text-[11px] mt-0.5 italic">
                    {info.subtitle || "3 min"}
                  </p>
                </div>
              </div>
              <div className="mt-5">
                <div className="h-1.5 rounded-full bg-white/[0.08] overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-amber-300 to-amber-500 transition-all"
                    style={{ width: `${Math.min(100, progress * 100)}%` }}
                  />
                </div>
                <div className="flex items-center justify-between mt-1.5 text-[10px] text-white/50 tabular-nums">
                  <span>{fmt(elapsed)}</span>
                  <span>{fmt(Math.round(totalSec))}</span>
                </div>
              </div>
              <audio
                ref={audioRef}
                src={`${API}${info.audio_url}`}
                preload="metadata"
                data-testid="share-audio"
              />
            </>
          )}
        </div>

        {/* Soft CTA */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center gap-1.5 text-amber-200/70 text-[10px] uppercase tracking-[0.3em] mb-3">
            <Sparkles size={11} />
            <span>What is Reclaim?</span>
          </div>
          <p className="text-white/65 text-sm leading-relaxed mb-5 max-w-xs mx-auto">
            Reclaim is a daily rhythm for the mind, body and nervous system. Audios, rituals and resets — for people who want to live consciously.
          </p>
          <button
            onClick={() => navigate("/")}
            data-testid="share-cta"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-amber-400/95 hover:bg-amber-300 text-[#0B0805] text-sm font-medium transition"
          >
            Explore Reclaim
            <span className="text-base">→</span>
          </button>
        </div>

        <div className="flex-1" />

        <footer className="text-center text-white/30 text-[10px] uppercase tracking-[0.25em] py-6">
          Reclaim · School of Consciousness
        </footer>
      </main>
    </div>
  );
}
